import { Link } from 'react-router-dom';
import { useState } from 'react';
import {
  experienceCategories,
  verifiedProperties,
  type PropertyCommunity,
  type VerifiedProperty,
} from '@/mocks/properties';
import { propertyRoute } from '@/mocks/propertyRoutes';
import FavoriteButton from '@/components/feature/FavoriteButton';

function Highlights({ property }: { property: VerifiedProperty }) {
  return (
    <ul className="mt-6 space-y-2.5">
      {property.highlights.map((h) => (
        <li key={h} className="flex items-center gap-3 text-sm text-foreground-700">
          <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0" />
          {h}
        </li>
      ))}
    </ul>
  );
}

function ConciergeLink() {
  return (
    <a
      href="#concierge"
      className="relative z-10 inline-flex items-center gap-2 text-sm text-foreground-900 cursor-pointer whitespace-nowrap group"
    >
      <span className="border-b border-foreground-300 group-hover:border-foreground-950 transition-colors">
        Ask the Insider
      </span>
      <i className="ri-arrow-right-up-line" />
    </a>
  );
}

function PropertyImage({ property, className }: { property: VerifiedProperty; className?: string }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-background-200 ${className ?? ''}`}>
      <img
        src={property.image}
        alt={property.name}
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/30 via-transparent to-transparent" />
    </div>
  );
}

type AreaFilter = 'All' | PropertyCommunity;

const areaFilters: AreaFilter[] = [
  'All',
  'Panama City Beach',
  '30A',
  'Rosemary Beach',
  'WaterSound',
  'Dune Allen',
  'Santa Rosa Beach',
];

const areaRoutes: Record<AreaFilter, string> = {
  All: '/vacation-search-results',
  'Panama City Beach': '/pcb-area-search',
  '30A': '/30a-area-search',
  'Rosemary Beach': '/rosemary-beach-search',
  WaterSound: '/watersound',
  Watercolor: '/watercolor-area',
  'Dune Allen': '/dune-allen',
  'Santa Rosa Beach': '/santa-rosa-beach',
};

const amenityRoutes: Record<string, string> = {
  'gulf-front': '/gulf-front-search-results',
  pool: '/pool-search-results',
  'large-groups': '/large-groups-search-results',
  'outdoor-living': '/outdoor-living-search-results',
  'golf-cart': '/golf-cart-search-results',
  'pet-friendly': '/pets-search-results',
};

const inactiveChipStyle =
  'bg-[#EFE4D6] text-foreground-700 border-[#DED0BE] hover:border-[#B99F87] hover:text-foreground-950';

export default function FeaturedProperties() {
  const [selectedArea, setSelectedArea] = useState<AreaFilter>('All');
  const availableAreaFilters = areaFilters.filter(
    (area) => area === 'All' || verifiedProperties.some((p) => p.communities.includes(area)),
  );
  const displayedProperties =
    selectedArea === 'All'
      ? verifiedProperties
      : verifiedProperties.filter((p) => p.communities.includes(selectedArea));
  const featured = displayedProperties[0];
  const supporting = displayedProperties.slice(1, 5);

  return (
    <section
      id="featured"
      className="relative bg-background-100/60 py-20 md:py-32 border-y border-background-200/70"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.24em] text-accent-700 mb-4">
              <span className="inline-block w-6 h-px bg-accent-500 align-middle mr-3" />
              Our Vacation Properties
            </div>
            <h2 className="font-heading text-foreground-950 text-4xl md:text-6xl leading-[1.05] tracking-[-0.01em]">
              <span className="block md:inline">A unique collection of</span>
              <span className="block md:hidden">vacation properties</span>
              <span className="block md:hidden italic font-light text-foreground-600">inspired by the sea.</span>
              <span className="hidden md:inline">{' vacation properties '}</span>
              <span className="hidden md:inline italic font-light text-foreground-600">inspired by the sea.</span>
            </h2>
            <p className="mt-6 max-w-xl text-sm md:text-base text-foreground-600">
              Tap the <span className="text-red-600" aria-hidden="true">♥</span>
              <span className="sr-only">heart</span> on any stay to save your favorites and compare them as you browse.
            </p>
          </div>
          <div className="md:max-w-lg space-y-5">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-700 mb-3">
                Explore by area
              </p>
              <div
                className="flex flex-wrap items-center gap-2"
                aria-label="Filter properties by area"
              >
                {availableAreaFilters.map((label) => {
                  const active = selectedArea === label;

                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setSelectedArea(label)}
                      className={`text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-lg border cursor-pointer transition-colors whitespace-nowrap ${
                        active
                          ? 'bg-foreground-950 text-background-50 border-foreground-950'
                          : inactiveChipStyle
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-700 mb-3">
                Explore by amenities
              </p>
              <div
                className="flex flex-wrap items-center gap-2"
                aria-label="Explore properties by amenity"
              >
                {experienceCategories.map((category) => (
                  <Link
                    key={category.id}
                    to={amenityRoutes[category.id] ?? '/vacation-search-results'}
                    className={`text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-lg border cursor-pointer transition-colors whitespace-nowrap ${inactiveChipStyle}`}
                  >
                    {category.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Featured property */}
        <article className="relative group mb-14 md:mb-16">
          <Link
            to={propertyRoute(featured.id)}
            aria-label={`View ${featured.name}`}
            className="absolute inset-0 z-[1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/70"
          />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 relative">
              <PropertyImage
                property={featured}
                className="aspect-[4/5] md:aspect-[16/12] lg:aspect-[4/5] lg:min-h-[560px]"
              />
              <FavoriteButton id={featured.id} className="absolute top-4 right-4 z-10" />
            </div>
            <div className="lg:col-span-5 flex flex-col justify-center">
              <div className="text-[11px] uppercase tracking-[0.22em] text-foreground-500 mb-2">
                {featured.location}
              </div>
              <h3 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
                {featured.name}
              </h3>
              <p className="mt-4 text-foreground-600 text-base md:text-lg font-light leading-relaxed max-w-md">
                {featured.blurb}
              </p>
              <Highlights property={featured} />
              <div className="mt-8 flex items-center gap-5">
                <span className="inline-flex items-center gap-2 text-sm bg-foreground-950 text-background-50 px-5 py-2.5 rounded-lg whitespace-nowrap">
                  View {featured.name}
                  <i className="ri-arrow-right-line" />
                </span>
                <ConciergeLink />
              </div>
            </div>
          </div>
        </article>

        {/* Supporting properties */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {supporting.map((p) => (
            <article key={p.id} className="relative group">
              <Link
                to={propertyRoute(p.id)}
                aria-label={`View ${p.name}`}
                className="absolute inset-0 z-[1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/70"
              />
              <div className="relative">
                <PropertyImage property={p} className="aspect-[4/5] md:aspect-[3/4]" />
                <FavoriteButton id={p.id} className="absolute top-4 right-4 z-10" />
              </div>
              <div className="pt-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-foreground-500 mb-2">
                  {p.location}
                </div>
                <h3 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight">
                  {p.name}
                </h3>
                <p className="mt-3 text-foreground-600 text-sm md:text-base font-light leading-relaxed max-w-md">
                  {p.blurb}
                </p>
                <Highlights property={p} />
                <div className="mt-7 flex items-center gap-5">
                  <span className="inline-flex items-center gap-2 text-sm bg-foreground-950 text-background-50 px-5 py-2.5 rounded-lg whitespace-nowrap">
                    View {p.name}
                    <i className="ri-arrow-right-line" />
                  </span>
                  <ConciergeLink />
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <div className="border border-background-300 bg-background-50/70 rounded-lg p-5 md:p-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-700 mb-3">
              Explore by area
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {availableAreaFilters.map((label) => (
                <Link
                  key={label}
                  to={areaRoutes[label]}
                  className={`text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-lg border cursor-pointer transition-colors whitespace-nowrap ${inactiveChipStyle}`}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>

          <div className="border border-background-300 bg-background-50/70 rounded-lg p-5 md:p-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-foreground-700 mb-3">
              Explore by amenities
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {experienceCategories.map((category) => (
                <Link
                  key={category.id}
                  to={amenityRoutes[category.id] ?? '/vacation-search-results'}
                  className="text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-lg border bg-[#EFE4D6] text-foreground-700 border-[#DED0BE] hover:border-[#B99F87] hover:text-foreground-950 cursor-pointer transition-colors whitespace-nowrap"
                >
                  {category.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
